import { z } from "zod";
import type { SessionResult } from "@cwb/core";
import { SessionSpeedSchema } from "./worldSchemas";
import type { WorldSnapshot } from "./worldSchemas";

/**
 * Everything the browser can say to the server. Player intent only — never
 * money, never inventory, never territory. Each of these is re-validated
 * against world state inside the core before it changes anything.
 */

export const AiModeSchema = z.enum(["mock", "record", "replay", "live", "hybrid"]);

export const CreateSessionRequestSchema = z.object({
  seed: z.string().min(1).max(64).optional(),
  aiMode: AiModeSchema.optional(),
  /**
   * Build the board but hold the clock, until POST /sessions/:id/start.
   *
   * The briefing is read over the opening position, so the position has to
   * exist while it is read — and a war that advanced during the briefing would
   * let the briefing consume simulation time. Omitted, the war starts at once,
   * which is what every non-browser caller wants.
   */
  paused: z.boolean().optional(),
});
export type CreateSessionRequest = z.infer<typeof CreateSessionRequestSchema>;

/** The only four clock states the game exposes. */
export const SessionSpeedRequestSchema = z
  .object({
    speed: SessionSpeedSchema,
  })
  .strict();
export type SessionSpeedRequest = z.infer<typeof SessionSpeedRequestSchema>;

/**
 * `onCredit` only means anything on the sell route: it asks the server to fill
 * the whole quantity and book whatever the nation's treasury cannot cover
 * against its credit line. The buy route ignores it — the broker always pays cash.
 */
export const MarketTradeRequestSchema = z.object({
  orderId: z.string().min(1).max(64),
  quantity: z.number().int().min(1).max(200),
  onCredit: z.boolean().optional(),
  /** Hand the goods over for nothing — the rung below credit. Sales only. */
  asGift: z.boolean().optional(),
  /**
   * The ground the trade is made on: where a sale unloads, where a purchase
   * loads. Omitted, both fall back to the nation's depot — which is what every
   * trade did before the counterparty became a province rather than a flag.
   */
  fromProvinceId: z.string().min(1).max(64).optional(),
  toProvinceId: z.string().min(1).max(64).optional(),
});
export type MarketTradeRequest = z.infer<typeof MarketTradeRequestSchema>;

/**
 * A gift names a province, not an order. That is the only request the player
 * can make of a nation that has stopped asking for anything — and the province
 * is what makes it interesting: crates in the province holding the line change
 * that line, crates in the depot are a donation to a warehouse.
 */
export const BrokerGiftRequestSchema = z.object({
  nationId: z.string().min(1).max(32),
  provinceId: z.string().min(1).max(64),
  goodType: z.enum(["weapons", "resources"]),
  quantity: z.number().int().min(1).max(200),
});
export type BrokerGiftRequest = z.infer<typeof BrokerGiftRequestSchema>;

export const SessionIdParamSchema = z.object({
  sessionId: z.string().min(1).max(64),
});

/**
 * The broker company's display name, chosen once per browser installation.
 *
 * It is flavor only: it names the broker's ship on the map. It never leaves the
 * browser (no API carries it) and is not a ranking identity — the portal owns
 * that. Twenty visible characters fit the ship label without crowding the map.
 * Markup, quotes, controls and invisible formatting characters are refused so
 * the value is safe to render anywhere in the game.
 */
export const CompanyNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(20)
  .refine((value) => !/[<>&"'`\p{Cc}\p{Cf}]/u.test(value), "contains a forbidden character");
export type CompanyName = z.infer<typeof CompanyNameSchema>;

// ---------------------------------------------------------------------------
// responses
// ---------------------------------------------------------------------------

export interface ApiError {
  ok: false;
  error: string;
}

export interface CreateSessionResponse {
  ok: true;
  sessionId: string;
  snapshot: WorldSnapshot;
  /**
   * Why this session is not running the mode that was asked for, if it is not.
   *
   * The server decides this — no credential, or every live slot already taken —
   * and it used to keep the answer to itself in a console warning. On screen the
   * paid switch simply was not there, with a tooltip guessing at a reason that
   * could be the wrong one. A downgrade the player cannot see is a downgrade
   * they will report as a missing button.
   */
  aiModeNote?: string | null;
}

export interface SnapshotResponse {
  ok: true;
  snapshot: WorldSnapshot;
}

export interface SessionSpeedResponse {
  ok: true;
  speed: z.infer<typeof SessionSpeedSchema>;
}

export interface TradeResponse {
  ok: true;
  quantity: number;
  unitPrice: number;
  totalValue: number;
}

export interface HealthResponse {
  ok: true;
  status: "healthy";
  aiMode: string;
  activeSessions: number;
  uptimeSeconds: number;
  version: string;
  /** RULESET_VERSION of the simulation this server runs; scores are only comparable within one. */
  rules: string;
}

export interface SessionResultResponse {
  ok: true;
  result: SessionResult;
}

export type ApiResponse<T> = T | ApiError;
